import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import styles from "./EditRolePage.module.scss";
import { AxiosResponse, AxiosErrorType } from "../../../src/types";

interface FormValues {
  name: string;
  permissions: Record<string, boolean>;
  issuperadmin: boolean;
}

const EditUserTypePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formValues, setFormValues] = useState<FormValues>({
    name: "",
    permissions: {},
    issuperadmin: false,
  });
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.post<AxiosResponse<{ usertypes: any[] }>>("/core/role/usertype/get", {});
        const userTypeData = response.data.usertypes?.find((ut) => ut.id === parseInt(id || "0"));
        if (!userTypeData) {
          console.error("User type not found for id:", id);
          throw new Error("User type not found");
        }
        setFormValues({
          name: userTypeData.name,
          permissions: userTypeData.permissions || {},
          issuperadmin: userTypeData.issuperadmin || false,
        });
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || "Failed to fetch user type";
        console.error("Fetch user type failed:", err.message, err.response?.data);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePermissionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setFormValues((prev) => ({
        ...prev,
        permissions: JSON.parse(e.target.value || "{}"),
      }));
    } catch (err) {
      setError("Invalid JSON format for permissions");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const response = await api.post("/core/role/usertype/update", {
        usertype_id: parseInt(id || "0"),
        name: formValues.name,
        permissions: formValues.permissions,
        issuperadmin: formValues.issuperadmin,
      });
      console.log("User type updated:", response.data);
      navigate("/roles");
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to update user type";
      console.error("Update user type failed:", err.message, err.response?.data);
      setError(errorMessage);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <h2>Edit User Type</h2>
      {error && <div className={styles.error}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="name">User Type Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formValues.name}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="issuperadmin">
            <input
              type="checkbox"
              id="issuperadmin"
              name="issuperadmin"
              checked={formValues.issuperadmin}
              onChange={handleInputChange}
            />
            Superadmin
          </label>
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="permissions">Permissions (JSON)</label>
          <input
            type="text"
            id="permissions"
            name="permissions"
            value={JSON.stringify(formValues.permissions)}
            onChange={handlePermissionsChange}
            placeholder='{"view": true, "edit": false}'
          />
        </div>
        <button type="submit">Update User Type</button>
      </form>
    </div>
  );
};

export default EditUserTypePage;
